import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Loader2, Minus, PartyPopper, Plus, Send, ShoppingBag, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { useCart } from '../utils/cart';
import { useCurrency } from '../utils/currency';
import { API } from '../utils/operations';
import { ConsentCheckboxes } from './ConsentCheckboxes';
import { EstimatePrice } from './EstimatePrice';
import { PriceOnRequest } from './PriceOnRequest';
import { Reveal } from './Reveal';

const inputCls = 'eco-input';

const TYPE_LINKS = {
  experience: (slug) => `/experiences/${slug}`,
  accommodation: (slug) => `/accommodations/${slug}`,
  transfer: () => '/transfers',
  package: (slug) => `/journeys/${slug}`,
  'rent-a-car': (slug) => `/rent-a-car/${slug}`,
};

const CartItem = ({ item, testid }) => {
  const { t } = useLang();
  const { remove, update } = useCart();
  return (
    <article data-testid={testid} className="rounded-2xl border border-eco-ink/10 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="eco-eyebrow">{t(`cart.type.${item.type}`)}</p>
          <Link to={TYPE_LINKS[item.type]?.(item.slug) || '/'} data-testid={`${testid}-link`} className="mt-1 block text-base font-semibold text-eco-ink hover:text-eco-green">
            {item.name}
          </Link>
          {item.destination && <p className="eco-meta mt-0.5 text-eco-ink/55">{item.destination}</p>}
        </div>
        <div className="text-right">
          {item.price_eur_snapshot
            ? <EstimatePrice testid={`${testid}-price`} amountEur={item.price_eur_snapshot} compact />
            : <PriceOnRequest compact testid={`${testid}-price`} />}
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <label>
          <span className="eco-field-label">{t('cart.qty')}</span>
          <span className="mt-1 flex items-center gap-2">
            <button type="button" data-testid={`${testid}-qty-minus`} aria-label="-" onClick={() => update(item.key, { qty: Math.max(1, (item.qty || 1) - 1) })} className="grid h-9 w-9 place-items-center rounded-lg border border-eco-ink/15 text-eco-ink/60 transition-colors hover:border-eco-gold hover:text-eco-green"><Minus size={14} /></button>
            <span data-testid={`${testid}-qty`} className="eco-number min-w-8 text-center text-sm font-semibold">{item.qty || 1}</span>
            <button type="button" data-testid={`${testid}-qty-plus`} aria-label="+" onClick={() => update(item.key, { qty: Math.min(50, (item.qty || 1) + 1) })} className="grid h-9 w-9 place-items-center rounded-lg border border-eco-ink/15 text-eco-ink/60 transition-colors hover:border-eco-gold hover:text-eco-green"><Plus size={14} /></button>
          </span>
        </label>
        <label>
          <span className="eco-field-label">{t('cart.dates')}</span>
          <input data-testid={`${testid}-dates`} value={item.dates || ''} onChange={(e) => update(item.key, { dates: e.target.value })} placeholder={t('cart.dates.ph')} className={`${inputCls} mt-1`} />
        </label>
        <label>
          <span className="eco-field-label">{t('cart.passengers')}</span>
          <input data-testid={`${testid}-passengers`} value={item.passengers || ''} onChange={(e) => update(item.key, { passengers: e.target.value })} placeholder="2" className={`${inputCls} mt-1`} />
        </label>
      </div>
      <div className="mt-3 flex justify-end">
        <button type="button" data-testid={`${testid}-remove`} onClick={() => remove(item.key)} className="inline-flex min-h-9 items-center gap-1.5 text-xs font-semibold text-eco-ink/40 transition-colors hover:text-red-500">
          <Trash2 size={14} /> {t('cart.remove')}
        </button>
      </div>
    </article>
  );
};

export const TripCart = () => {
  const { t } = useLang();
  const { items, clear, getRequestId, rotateRequestId, mayOverlapPackage } = useCart();
  const { currency } = useCurrency();
  const [contact, setContact] = useState({ name: '', email: '', whatsapp: '', message: '' });
  const [consents, setConsents] = useState({ terms: false, cancellation: false, marketing: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  if (done) {
    return (
      <Reveal className="mt-8" data-testid="trip-cart-success">
        <div className="rounded-2xl border border-eco-green/20 bg-white p-8 text-center shadow-sm">
          <PartyPopper size={36} className="mx-auto text-eco-gold" />
          <h2 className="mt-4 text-2xl leading-tight font-semibold tracking-[-0.02em] text-eco-ink">{t('cart.success.t')}</h2>
          <p data-testid="trip-cart-reference" className="eco-number mt-3 text-3xl font-bold tracking-[-0.02em] text-eco-green">{done.reference}</p>
          <p className="mx-auto mt-4 max-w-md rounded-xl bg-eco-gold/10 px-4 py-3 text-xs font-semibold text-eco-ink/70">{t('cart.success.note')}</p>
        </div>
      </Reveal>
    );
  }

  if (items.length === 0) {
    return (
      <Reveal className="mt-8" data-testid="trip-cart-empty">
        <div className="rounded-2xl border border-dashed border-eco-ink/15 bg-white/60 px-6 py-10 text-center">
          <ShoppingBag size={28} className="mx-auto text-eco-ink/25" />
          <p className="mt-3 text-sm text-eco-ink/55">{t('cart.empty')}</p>
          <Link to="/experiences" data-testid="trip-cart-continue" className="eco-btn-text mt-4 justify-center">{t('cart.continue')}</Link>
        </div>
      </Reveal>
    );
  }

  const destinations = [...new Set(items.map((i) => i.destination).filter(Boolean))];
  const withPrice = items.filter((i) => i.price_eur_snapshot).length;

  const submit = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'trip',
          name: contact.name,
          email: contact.email,
          whatsapp: contact.whatsapp,
          message: contact.message,
          destination: destinations.join(' + '),
          cart_items: items.map(({ type, slug, name, destination, qty, dates, passengers, notes }) => ({ type, slug, name, destination, qty, dates, passengers, notes })),
          cart_currency: currency,
          client_request_id: getRequestId(),
          consent_terms: consents.terms,
          consent_cancellation: consents.cancellation,
          consent_marketing: consents.marketing,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'failed');
      }
      const created = await res.json();
      rotateRequestId();
      clear();
      setDone(created);
    } catch (err) {
      toast.error(t('cart.error'), { description: String(err.message || '') });
    } finally {
      setSending(false);
    }
  };

  return (
    <Reveal className="mt-8" data-testid="trip-cart">
      <div className="flex items-center gap-2.5">
        <ShoppingBag size={20} className="text-eco-gold" />
        <h2 className="font-serif text-xl font-semibold text-eco-ink">{t('cart.title')}</h2>
        <span data-testid="trip-cart-count" className="rounded-full bg-eco-gold/15 px-2.5 py-0.5 text-xs font-bold text-eco-deep">{items.length}</span>
      </div>

      <div className="mt-5 space-y-4">
        {items.map((item) => <CartItem key={item.key} item={item} testid={`cart-item-${item.type}-${item.slug}`} />)}
      </div>

      {mayOverlapPackage && (
        <p data-testid="trip-cart-overlap-note" className="mt-4 rounded-xl bg-eco-gold/10 px-4 py-3 text-xs font-medium text-eco-ink/70">{t('cart.package.overlap')}</p>
      )}

      <div data-testid="trip-cart-summary" className="mt-6 rounded-2xl border border-eco-ink/10 bg-white p-6">
        <h3 className="text-base font-semibold text-eco-ink">{t('cart.summary')}</h3>
        <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-3">
          <div><dt className="eco-field-label">{t('cart.destinations')}</dt><dd data-testid="cart-summary-destinations" className="mt-1 font-medium text-eco-ink">{destinations.join(' · ') || '—'}</dd></div>
          <div><dt className="eco-field-label">{t('cart.passengers')}</dt><dd data-testid="cart-summary-passengers" className="mt-1 flex items-center gap-1.5 font-medium text-eco-ink"><Users size={14} className="text-eco-gold" /> {items.map((i) => i.passengers).filter(Boolean).join(' · ') || '—'}</dd></div>
          <div><dt className="eco-field-label">{t('cart.pricing.status')}</dt><dd data-testid="cart-summary-pricing" className="mt-1 flex items-center gap-1.5 font-medium text-eco-ink"><Calendar size={14} className="text-eco-gold" /> {withPrice > 0 ? `${withPrice} ${t('cart.pricing.estimates')} · ${items.length - withPrice} ${t('cart.pricing.onrequest')}` : t('price.onrequest')}</dd></div>
        </dl>
        <p className="mt-4 text-[11px] leading-relaxed text-eco-ink/45">{t('cart.estimate.note')}</p>
      </div>

      <div className="mt-6 rounded-2xl border border-eco-ink/10 bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <input data-testid="cart-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder={t('form.name')} className={inputCls} />
          <input data-testid="cart-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder={t('form.email')} className={inputCls} />
          <input data-testid="cart-whatsapp" value={contact.whatsapp} onChange={(e) => setContact({ ...contact, whatsapp: e.target.value })} placeholder={t('wiz.wa.ph')} className={inputCls} />
        </div>
        <textarea data-testid="cart-message" rows={2} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder={t('cart.notes.ph')} className={`${inputCls} mt-4`} />
        <div className="mt-4">
          <ConsentCheckboxes testid="cart-consent" value={consents} onChange={setConsents} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="cart-submit"
            onClick={submit}
            disabled={sending || !contact.name || !contact.email || !consents.terms}
            className="inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-eco-gold px-6 text-[15px] font-semibold text-eco-deep transition-colors duration-200 hover:bg-[#b8944f] disabled:opacity-50"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} {t('cart.submit')}
          </button>
          <button type="button" data-testid="cart-clear" onClick={clear} className="eco-btn-text text-eco-ink/50 hover:text-red-500">{t('cart.clear')}</button>
        </div>
      </div>
    </Reveal>
  );
};
